/**
 * Navigation and binary controls for token-bound monograph panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import {
  MonographControlBar,
  MonographControlButton,
} from "./MonographPanelControls.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export interface MonographToggleChipProps {
  readonly tokens: MonographPanelTokens;
  readonly label: React.ReactNode;
  readonly value: boolean;
  readonly onChange: () => void;
  readonly color?: string;
  readonly className?: string;
  readonly title?: string;
}

export interface MonographStepperBarProps {
  readonly tokens: MonographPanelTokens;
  readonly cursor: number;
  readonly total: number;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onReset: () => void;
  readonly labelPrefix?: string;
  readonly className?: string;
}

export function MonographToggleChip({
  tokens,
  label,
  value,
  onChange,
  color = tokens.accent,
  className,
  title,
}: MonographToggleChipProps): React.JSX.Element {
  return (
    <label
      title={title}
      className={cn("csk-monograph-toggle-chip", className)}
      style={{
        alignItems: "center",
        background: value
          ? colorWithAlpha(color, "22")
          : colorWithAlpha(tokens.void, "66"),
        border: `1px solid ${value ? colorWithAlpha(color, "88") : tokens.border}`,
        borderRadius: 999,
        color: value ? color : tokens.muted,
        cursor: "pointer",
        display: "inline-flex",
        fontFamily: tokens.mono,
        fontSize: 12,
        fontWeight: 700,
        gap: 8,
        letterSpacing: 0,
        minHeight: 32,
        padding: "7px 12px",
        transition:
          "background 140ms ease, border-color 140ms ease, color 140ms ease",
      }}
    >
      <input
        type="checkbox"
        checked={value}
        onChange={onChange}
        style={{ accentColor: color, margin: 0 }}
      />
      <span>{label}</span>
    </label>
  );
}

export function MonographStepperBar({
  tokens,
  cursor,
  total,
  onPrev,
  onNext,
  onReset,
  labelPrefix = "step",
  className,
}: MonographStepperBarProps): React.JSX.Element {
  const atStart = cursor <= 0;
  const atEnd = cursor >= total;
  return (
    <MonographControlBar {...(className ? { className } : {})}>
      <MonographControlButton
        tokens={tokens}
        label="← prev"
        onClick={onPrev}
        disabled={atStart}
        tone="ghost"
        color={tokens.text}
      />
      <MonographControlButton
        tokens={tokens}
        label="next →"
        onClick={onNext}
        disabled={atEnd}
        tone="ghost"
        color={tokens.text}
      />
      <MonographControlButton
        tokens={tokens}
        label="reset"
        onClick={onReset}
        tone="ghost"
        color={tokens.muted}
      />
      <span
        style={{
          color: tokens.muted,
          fontFamily: tokens.mono,
          fontSize: 11,
          marginLeft: "auto",
        }}
      >
        {labelPrefix} {cursor} of {total}
      </span>
    </MonographControlBar>
  );
}
